import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import "../../demoMotion.css";
import "../../demoThemes.css";
import "../../demoComplete.css";
import { DemoBrowser, DemoContactBlock, DemoFullFooter } from "../../DemoFullParts";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

function MotionBlock({ children, className = "" }) {
  return (
    <motion.div className={className} {...fadeUp}>
      {children}
    </motion.div>
  );
}

function SharedFooter({ brand, industry }) {
  return (
    <DemoFullFooter
      brand={brand.name}
      desc={brand.slogan}
      columns={[
        { title: "서비스", links: ["소개", "핵심 기능", "요금"] },
        { title: "고객지원", links: ["FAQ", "문의", brand.phone ?? "연락처"] },
      ]}
      copy={`© 2026 ${brand.nameEn} · Portfolio by DSiteSolution`}
    />
  );
}

/* ── NOVA: 다크 벤토 + 오브 + 마퀴 ── */
export function NovaLayout({ brand, accent, features, cta, embed, industry }) {
  const marquee = [...features, ...features].map((f) => f.title);
  return (
    <div
      className={`demo-root demo-nova${embed ? " demo-root--embed" : ""}`}
      style={{ "--accent": accent, "--bg": "#050508", "--muted": "rgba(245,245,247,.55)", "--border": "rgba(255,255,255,.08)", "--section-alt": "#0a0a10" }}
    >
      <div className="demo-nova__orb demo-nova__orb--1" style={{ background: accent }} />
      <div className="demo-nova__orb demo-nova__orb--2" />
      <header className="demo-nova__nav">
        <div className="demo-nova__brand">{brand.name.split(" ")[0]}<span>{brand.nameEn?.split(" ")[0]}</span></div>
        <span className="demo-nova__btn demo-nova__btn--fill" style={{ background: accent }}>{cta}</span>
      </header>
      <MotionBlock>
        <section className="demo-nova__hero">
          <h1>{brand.slogan}</h1>
          <p>{brand.intro}</p>
          <div className="demo-nova__cta-row">
            <span className="demo-nova__btn demo-nova__btn--fill" style={{ background: accent }}>{cta}</span>
            <span className="demo-nova__btn demo-nova__btn--line">더 알아보기</span>
          </div>
          <div className="demo-nova__bento">
            <div className="demo-nova__bento-main">
              <span className="demo-full__eyebrow" style={{ color: accent }}>{brand.nameEn}</span>
              <h3 style={{ margin: "0.5rem 0", fontSize: "1.1rem" }}>{features[0]?.title}</h3>
              <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--muted)" }}>{features[0]?.desc}</p>
            </div>
            <div className="demo-nova__bento-side">
              <strong style={{ fontSize: "0.8rem" }}>{features[1]?.title}</strong>
              <p style={{ margin: "0.35rem 0 0", fontSize: "0.72rem", color: "var(--muted)" }}>{features[1]?.desc}</p>
            </div>
            {features.slice(2, 4).map((f) => (
              <div key={f.title} className="demo-nova__bento-wide">
                <strong style={{ fontSize: "0.78rem" }}>{f.title}</strong>
                <p style={{ margin: "0.25rem 0 0", fontSize: "0.68rem", color: "var(--muted)" }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </MotionBlock>
      <div className="demo-nova__marquee-wrap">
        <div className="demo-nova__marquee">
          {marquee.map((t, i) => <span key={`${t}-${i}`}>{t}</span>)}
        </div>
      </div>
      <div className="demo-nova__contact">
        <h2 style={{ margin: "0 0 0.5rem", fontSize: "1.25rem" }}>지금 시작하세요</h2>
        <p style={{ margin: "0 0 1rem", color: "var(--muted)", fontSize: "0.85rem" }}>{brand.address ?? brand.phone}</p>
        <span className="demo-nova__btn demo-nova__btn--fill" style={{ background: accent }}>{cta}</span>
      </div>
      <SharedFooter brand={brand} industry={industry} />
    </div>
  );
}

/* ── MERI: 에디토리얼 매거진 + 챕터 스크롤 ── */
export function MeriLayout({ brand, accent, features, cta, embed, industry }) {
  const grads = [
    `linear-gradient(145deg, ${accent}33, ${accent}66)`,
    "linear-gradient(145deg,#fce7f3,#fbcfe8)",
    "linear-gradient(145deg,#e0e7ff,#c7d2fe)",
  ];
  return (
    <div
      className={`demo-root demo-meri${embed ? " demo-root--embed" : ""}`}
      style={{ "--accent": accent, "--bg": "#faf8f5", "--muted": "#78716c", "--border": "#e7e5e4", "--section-alt": "#f5f3ef" }}
    >
      <header className="demo-meri__nav">
        <h1>{brand.name}</h1>
        <nav className="demo-full__nav-links" style={{ display: "flex", color: "#78716c" }}>
          <span>Story</span><span>Service</span><span>Contact</span>
        </nav>
        <span style={{ fontSize: "0.72rem", padding: "0.45rem 0.9rem", border: "1px solid #e7e5e4" }}>{cta}</span>
      </header>
      <section className="demo-meri__cover" style={{ background: `linear-gradient(180deg, ${accent}dd, ${accent})` }}>
        <MotionBlock><h2>{brand.slogan}</h2></MotionBlock>
      </section>
      <section style={{ padding: "2.5rem 1.5rem", maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
        <p style={{ margin: 0, fontFamily: "Georgia, serif", fontSize: "1.05rem", lineHeight: 1.8, color: "#57534e" }}>{brand.intro}</p>
      </section>
      <div className="demo-meri__chapters">
        {features.map((f, i) => (
          <motion.article
            key={f.title}
            className="demo-meri__chapter"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
          >
            <div className="demo-meri__chapter-img" style={{ background: grads[i % grads.length] }} />
            <div className="demo-meri__chapter-body">
              <em>0{i + 1} · Feature</em>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          </motion.article>
        ))}
      </div>
      <div className="demo-meri__quote">"{brand.slogan}" — {brand.name}</div>
      <div className="demo-meri__metrics">
        <div className="demo-meri__metric"><strong>10+</strong><span>Years</span></div>
        <div className="demo-meri__metric"><strong>98%</strong><span>Satisfaction</span></div>
        <div className="demo-meri__metric"><strong>24h</strong><span>Response</span></div>
      </div>
      <SharedFooter brand={brand} industry={industry} />
    </div>
  );
}

/* ── PULSE: 라이브 대시보드 + 애니메이션 차트 ── */
export function PulseLayout({ brand, accent, features, cta, embed, industry }) {
  const [val, setVal] = useState(0);
  const bars = [42, 58, 48, 72, 65, 88, 76, 95, 68, 82];
  useEffect(() => {
    const steps = [12, 28, 42, 58, 72];
    let i = 0;
    const id = setInterval(() => {
      if (i < steps.length) { setVal(steps[i]); i += 1; }
      else clearInterval(id);
    }, 400);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className={`demo-root demo-pulse${embed ? " demo-root--embed" : ""}`}
      style={{ "--accent": accent, "--bg": "#030712", "--muted": "#94a3b8", "--border": "#1e293b", "--card": "#0f172a", "--section-alt": "#0a0f1a" }}
    >
      <header className="demo-full__nav">
        <div style={{ fontWeight: 800 }}>{brand.name.split(" ")[0]} <span style={{ color: accent }}>Live</span></div>
        <nav className="demo-full__nav-links"><span>대시보드</span><span>리포트</span><span>연동</span></nav>
        <span className="demo-full__nav-cta" style={{ background: accent }}>{cta}</span>
      </header>
      <section className="demo-full__hero" style={{ background: `linear-gradient(180deg, ${accent}14, transparent)` }}>
        <div className="demo-full__hero-grid">
          <MotionBlock>
            <span className="demo-full__eyebrow">{brand.nameEn}</span>
            <h1 style={{ color: "#f1f5f9" }}>{brand.slogan}</h1>
            <p className="demo-full__hero-lead">{brand.intro}</p>
            <span className="demo-pulse__live">● LIVE 데이터</span>
          </MotionBlock>
          <DemoBrowser url={`app.${brand.nameEn?.toLowerCase().replace(/\s/g, "")}.io`} accent={accent}>
            <div style={{ padding: "1rem", background: "#030712" }}>
              <div className="demo-pulse__grid" style={{ padding: 0, marginBottom: "0.75rem" }}>
                {[{ l: "지표", v: `${val}%` }, { l: "방문", v: "18.2K" }, { l: "전환", v: "5.1%" }, { l: "만족", v: "96%" }].map((k) => (
                  <div key={k.l} className="demo-pulse__kpi"><small>{k.l}</small><strong>{k.v}</strong></div>
                ))}
              </div>
              <div className="demo-pulse__bars" style={{ height: 90 }}>
                {bars.map((h, i) => (
                  <div key={i} className="demo-pulse__bar" style={{ height: `${h}%`, animationDelay: `${i * 0.05}s`, background: accent }} />
                ))}
              </div>
            </div>
          </DemoBrowser>
        </div>
      </section>
      <section className="demo-full__section demo-full__section--alt">
        <div className="demo-full__wrap">
          <div className="demo-full__grid3">
            {features.map((f) => (
              <article key={f.title} className="demo-full__card">
                <h3 style={{ color: "#f1f5f9" }}>{f.title}</h3>
                <p>{f.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <SharedFooter brand={brand} industry={industry} />
    </div>
  );
}

/* ── STUDY: 따뜻한 UI + 프로그레스 + 브라우저 패널 ── */
export function StudyLayout({ brand, accent, features, cta, embed, industry }) {
  const progress = [78, 62, 91, 55];
  return (
    <div
      className={`demo-root demo-study${embed ? " demo-root--embed" : ""}`}
      style={{ "--accent": accent, "--bg": "#f4f7f5", "--muted": "#5c7268", "--border": "#dce8e0", "--card": "#fff", "--section-alt": "#eaf2ec" }}
    >
      <header className="demo-full__nav" style={{ background: "#fff", boxShadow: "0 2px 12px rgba(26,46,36,.06)" }}>
        <div className="demo-study__logo">
          <span className="demo-study__logo-icon" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}>
            {brand.name[0]}
          </span>
          {brand.name}
        </div>
        <nav className="demo-full__nav-links" style={{ display: "flex", color: "#5c7268" }}>
          <span>소개</span><span>서비스</span><span>문의</span>
        </nav>
        <span className="demo-full__nav-cta" style={{ background: accent }}>{cta}</span>
      </header>
      <section className="demo-full__hero" style={{ background: `linear-gradient(180deg, ${accent}18, #f4f7f5)` }}>
        <div className="demo-full__hero-grid">
          <MotionBlock>
            <span className="demo-full__eyebrow">{brand.nameEn}</span>
            <h1 style={{ color: "#1a2e24" }}>{brand.slogan}</h1>
            <p className="demo-full__hero-lead" style={{ color: "#5c7268" }}>{brand.intro}</p>
            <div className="demo-full__hero-btns">
              <span className="demo-full__btn demo-full__btn--primary" style={{ background: accent }}>{cta}</span>
            </div>
          </MotionBlock>
          <DemoBrowser url={brand.nameEn?.toLowerCase().replace(/\s/g, "") + ".co.kr"} accent={accent}>
            <div style={{ padding: "1rem", background: "#f4f7f5" }}>
              <div style={{ padding: "1rem", borderRadius: 14, background: `linear-gradient(135deg, ${accent}, ${accent}cc)`, color: "#fff", marginBottom: "0.75rem" }}>
                <strong>{brand.name}</strong>
                <p style={{ margin: "0.25rem 0 0", fontSize: "0.72rem", opacity: 0.9 }}>오늘의 일정 · 실시간 안내</p>
              </div>
              {features.map((f, i) => (
                <div key={f.title} style={{ padding: "0.75rem", marginBottom: "0.5rem", borderRadius: 12, background: "#fff", border: "1px solid #dce8e0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.78rem", fontWeight: 700, color: "#1a2e24" }}>
                    <span>{f.title}</span><span style={{ color: accent }}>{progress[i]}%</span>
                  </div>
                  <div className="demo-study__progress"><span style={{ width: `${progress[i]}%`, background: accent }} /></div>
                </div>
              ))}
            </div>
          </DemoBrowser>
        </div>
      </section>
      <div className="demo-full__marquee" style={{ background: "#fff" }}>
        <div className="demo-full__marquee-track" style={{ color: "#9cb0a4" }}>
          {features.map((f) => <span key={f.title}>{f.title}</span>)}
          {features.map((f) => <span key={`${f.title}-2`}>{f.title}</span>)}
        </div>
      </div>
      <SharedFooter brand={brand} industry={industry} />
    </div>
  );
}

/* ── OPS: 관리자 콘솔 + 토스트 + 테이블 ── */
export function OpsLayout({ brand, accent, features, cta, embed, industry }) {
  const [toast, setToast] = useState(true);
  const rows = features.map((f, i) => ({ name: f.title, role: f.desc.slice(0, 12), status: i % 2 ? "wait" : "ok" }));

  return (
    <div
      className={`demo-root demo-ops${embed ? " demo-root--embed" : ""}`}
      style={{ "--accent": accent, "--bg": "#0a1018", "--muted": "#64748b", "--border": "#1e293b", "--card": "#0f1724", "--section-alt": "#070d14", position: "relative" }}
    >
      {toast && (
        <div className="demo-ops__toast">
          {brand.name} — 신규 문의 접수
          <button type="button" onClick={() => setToast(false)} style={{ marginLeft: 8, background: "none", border: "none", color: "inherit", cursor: "pointer" }}>×</button>
        </div>
      )}
      <header className="demo-full__nav">
        <div style={{ fontWeight: 800, color: accent }}>{brand.name}</div>
        <nav className="demo-full__nav-links"><span>콘솔</span><span>리포트</span><span>설정</span></nav>
        <span className="demo-full__nav-cta" style={{ background: accent, color: "#0a1018" }}>{cta}</span>
      </header>
      <section className="demo-full__hero">
        <div className="demo-full__hero-grid">
          <MotionBlock>
            <span className="demo-full__eyebrow">{brand.nameEn}</span>
            <h1 style={{ color: "#e2e8f0" }}>{brand.slogan}</h1>
            <p className="demo-full__hero-lead">{brand.intro}</p>
          </MotionBlock>
          <DemoBrowser url={`admin.${brand.nameEn?.toLowerCase().replace(/\s/g, "")}.io`} accent={accent}>
            <div className="demo-ops__shell" style={{ minHeight: 240 }}>
              <aside className="demo-ops__sidebar">
                {features.map((f, i) => (
                  <span key={f.title} className={`demo-ops__nav-item${i === 0 ? " demo-ops__nav-item--on" : ""}`}>
                    <span className="demo-ops__nav-dot" />{f.title}
                  </span>
                ))}
              </aside>
              <main className="demo-ops__main">
                <div className="demo-ops__table-wrap">
                  <table>
                    <thead><tr><th>항목</th><th>설명</th><th>상태</th></tr></thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.name}>
                          <td>{r.name}</td><td>{r.role}</td>
                          <td><span className={`demo-ops__badge demo-ops__badge--${r.status}`}>{r.status === "ok" ? "운영" : "대기"}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </main>
            </div>
          </DemoBrowser>
        </div>
      </section>
      <SharedFooter brand={brand} industry={industry} />
    </div>
  );
}

/* ── CODE: IDE 터미널 + 점수 게이지 ── */
export function CodeLayout({ brand, accent, features, cta, embed, industry }) {
  const [score, setScore] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setScore(92), 700);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`demo-root demo-code${embed ? " demo-root--embed" : ""}`}
      style={{ "--accent": accent, "--bg": "#0b0f14", "--muted": "#64748b", "--border": "#1e293b", "--card": "#111820", "--section-alt": "#0d1117" }}
    >
      <header className="demo-full__nav">
        <div style={{ fontWeight: 800 }}>{brand.name}<span style={{ color: accent }}>.io</span></div>
        <nav className="demo-full__nav-links"><span>기능</span><span>API</span><span>문의</span></nav>
        <span className="demo-full__nav-cta" style={{ background: accent }}>{cta}</span>
      </header>
      <section className="demo-full__hero" style={{ background: `linear-gradient(180deg, ${accent}12, transparent)` }}>
        <div className="demo-full__hero-grid">
          <MotionBlock>
            <span className="demo-full__eyebrow">{brand.nameEn}</span>
            <h1 style={{ color: "#e2e8f0" }}>{brand.slogan}</h1>
            <p className="demo-full__hero-lead">{brand.intro}</p>
          </MotionBlock>
          <DemoBrowser url={`api.${brand.nameEn?.toLowerCase().replace(/\s/g, "")}.dev`} accent={accent}>
            <div className="demo-code__split" style={{ minHeight: 220 }}>
              <div className="demo-code__editor">
                <pre style={{ margin: 0, fontSize: "0.68rem" }}>{`// ${brand.name}\nconst status = "online";\nexport default { score: ${score} };`}</pre>
                <span className="demo-code__cursor demo-anim-blink" />
              </div>
              <div className="demo-code__review">
                <div className="demo-code__gauge">{score || "—"}</div>
                {features.slice(0, 2).map((f, i) => (
                  <div key={f.title} className={`demo-code__issue demo-code__issue--${i ? "ok" : "warn"}`}>{f.title}</div>
                ))}
              </div>
            </div>
          </DemoBrowser>
        </div>
      </section>
      <SharedFooter brand={brand} industry={industry} />
    </div>
  );
}

/* ── BLOOM: 소프트 플로럴 + 둥근 카드 ── */
export function BloomLayout({ brand, accent, features, cta, embed, industry }) {
  return (
    <div
      className={`demo-root demo-bloom${embed ? " demo-root--embed" : ""}`}
      style={{ "--accent": accent, "--bg": "#fff8fb", "--muted": "#9d8189", "--border": "#f5d0e0", "--section-alt": "#fef0f5" }}
    >
      <header className="demo-bloom__nav">
        <strong>{brand.name}</strong>
        <span className="demo-bloom__cta" style={{ background: accent }}>{cta}</span>
      </header>
      <section className="demo-bloom__hero">
        <div className="demo-bloom__petal demo-bloom__petal--1" style={{ background: `${accent}33` }} />
        <div className="demo-bloom__petal demo-bloom__petal--2" style={{ background: `${accent}22` }} />
        <MotionBlock>
          <span className="demo-full__eyebrow" style={{ color: accent }}>{brand.nameEn}</span>
          <h1>{brand.slogan}</h1>
          <p>{brand.intro}</p>
          <span className="demo-bloom__cta demo-bloom__cta--lg" style={{ background: accent }}>{cta}</span>
        </MotionBlock>
      </section>
      <section className="demo-bloom__cards">
        {features.map((f, i) => (
          <motion.article
            key={f.title}
            className="demo-bloom__card"
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
          >
            <h3>{f.title}</h3>
            <p>{f.desc}</p>
          </motion.article>
        ))}
      </section>
      <SharedFooter brand={brand} industry={industry} />
    </div>
  );
}

/* ── LUXE: 다크 럭셔리 + 골드 ── */
export function LuxeLayout({ brand, accent, features, cta, embed, industry }) {
  return (
    <div
      className={`demo-root demo-luxe${embed ? " demo-root--embed" : ""}`}
      style={{ "--accent": accent, "--gold": "#c9a962", "--bg": "#0d0b0a", "--muted": "#a89888", "--border": "#2a2420", "--section-alt": "#14100e" }}
    >
      <header className="demo-luxe__nav">
        <div><strong>{brand.name}</strong><span>{brand.nameEn}</span></div>
        <span className="demo-luxe__reserve">{cta}</span>
      </header>
      <section className="demo-luxe__hero">
        <MotionBlock>
          <p className="demo-luxe__eyebrow">{brand.nameEn}</p>
          <h1>{brand.slogan}</h1>
          <p className="demo-luxe__lead">{brand.intro}</p>
        </MotionBlock>
      </section>
      <section className="demo-luxe__grid">
        {features.map((f, i) => (
          <motion.div
            key={f.title}
            className="demo-luxe__item"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
          >
            <span className="demo-luxe__num">0{i + 1}</span>
            <h3>{f.title}</h3>
            <p>{f.desc}</p>
          </motion.div>
        ))}
      </section>
      <div className="demo-luxe__contact">
        <p>{brand.address}</p>
        <p>{brand.phone} · {brand.hours ?? brand.email}</p>
      </div>
      <SharedFooter brand={brand} industry={industry} />
    </div>
  );
}

/* ── ROAST: 커피/고기 다크 웜 + 메뉴보드 ── */
export function RoastLayout({ brand, accent, features, cta, embed, industry }) {
  return (
    <div
      className={`demo-root demo-roast${embed ? " demo-root--embed" : ""}`}
      style={{ "--accent": accent, "--bg": "#1a120e", "--muted": "#b8a090", "--border": "#3d2e24", "--section-alt": "#221810" }}
    >
      <header className="demo-roast__nav">
        <strong>{brand.name}</strong>
        <nav>{["메뉴", "스토리", "예약"].map((l) => <span key={l}>{l}</span>)}</nav>
        <span className="demo-roast__btn" style={{ borderColor: accent, color: accent }}>{cta}</span>
      </header>
      <section className="demo-roast__hero">
        <div className="demo-roast__steam" aria-hidden="true" />
        <MotionBlock>
          <h1>{brand.slogan}</h1>
          <p>{brand.intro}</p>
        </MotionBlock>
      </section>
      <section className="demo-roast__menu">
        <h2>Signature</h2>
        <div className="demo-roast__items">
          {features.map((f) => (
            <div key={f.title} className="demo-roast__item">
              <strong>{f.title}</strong>
              <span>{f.desc}</span>
            </div>
          ))}
        </div>
      </section>
      <SharedFooter brand={brand} industry={industry} />
    </div>
  );
}

/* ── MARBLE: 클래식 금융/의료 대리석 톤 ── */
export function MarbleLayout({ brand, accent, features, cta, embed, industry }) {
  return (
    <div
      className={`demo-root demo-marble${embed ? " demo-root--embed" : ""}`}
      style={{ "--accent": accent, "--bg": "#f8f9fb", "--muted": "#64748b", "--border": "#e2e8f0", "--section-alt": "#eef1f6" }}
    >
      <div className="demo-marble__top">
        <span>{brand.hours ?? "고객센터 운영 안내"}</span>
        <span>{brand.phone}</span>
      </div>
      <header className="demo-marble__nav">
        <div className="demo-marble__brand">
          <strong>{brand.name}</strong>
          <span>{brand.nameEn}</span>
        </div>
        <nav>{["상품", "서비스", "지점", "문의"].map((l) => <span key={l}>{l}</span>)}</nav>
        <span className="demo-marble__cta" style={{ background: accent }}>{cta}</span>
      </header>
      <section className="demo-marble__hero">
        <div className="demo-marble__columns" aria-hidden="true" />
        <MotionBlock>
          <h1>{brand.slogan}</h1>
          <p>{brand.intro}</p>
          <div className="demo-marble__trust">
            <div><strong>AAA</strong><span>신뢰등급</span></div>
            <div><strong>24h</strong><span>고객지원</span></div>
            <div><strong>100+</strong><span>전국 네트워크</span></div>
          </div>
        </MotionBlock>
      </section>
      <section className="demo-marble__services">
        {features.map((f) => (
          <article key={f.title} className="demo-marble__card">
            <h3>{f.title}</h3>
            <p>{f.desc}</p>
            <span style={{ color: accent }}>자세히 →</span>
          </article>
        ))}
      </section>
      <section className="demo-full__section" style={{ padding: "2rem 1.5rem" }}>
        <DemoContactBlock title="상담 신청" sub={brand.intro} fields={["이름", "연락처", "문의 내용"]} btnLabel={cta} />
      </section>
      <SharedFooter brand={brand} industry={industry} />
    </div>
  );
}

export const THEME_LAYOUTS = {
  nova: NovaLayout,
  meri: MeriLayout,
  pulse: PulseLayout,
  study: StudyLayout,
  ops: OpsLayout,
  code: CodeLayout,
  bloom: BloomLayout,
  luxe: LuxeLayout,
  roast: RoastLayout,
  marble: MarbleLayout,
};
